import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { FormLabel, FormControl, Col, Row, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Api from '../../services/ApiService';
import LoadingPage from '../LoadingPage';
import Form from '../form/Form';
import { modal } from '../modal/ModalEmitter';
import { getPageTitles } from '../../util/page';
import browserHistory from '../../history';

const eqsRoles = [
  {
    label: 'Sales Manager',
    value: 'SM',
  },
  {
    label: 'Sales Person',
    value: 'SP',
  },
  {
    label: 'Accounting',
    value: 'A',
  },
];

class UST3R extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeBranch: {},
      error: '',
      isNew: false,
      isReady: false,
      hasSubmitted: false,
      allBranches: 0,
      permissions: {},
      user: {},
    };

    // Ref to FEC
    this.form = React.createRef();

    // DB obj for each branch
    this.branches = {};

    // Initial FEC layout for each branch
    this.layouts = {};

    // keeps track state of roles/allow/deny for each branch
    this.permissionData = {};
  }

  componentDidMount() {
    Promise.all([this.initLayouts(), this.getUser()]).then(() => {
      this.setState({ isReady: true, isNew: location.search.includes('new') });
    });
  }

  getUser() {
    const { userId } = this.props;
    return Api.get(`db/UST3/${userId}`).then(res => {
      this.setState({ user: res.data });
    });
  }

  getCancelUrl() {
    return `/UST3/${this.props.userId}`;
  }

  getCurrentLayout = () => {
    return this.layouts[this.state.activeBranch.branchId];
  };

  getPermissionMap(data, activeBranch) {
    activeBranch = activeBranch || this.state.activeBranch;

    return Api.get('permissionmap', {
      params: {
        roles: data.roles.join(','),
        allow: data.allow.join(','),
        deny: data.deny.join(','),
      },
    })
      .then(res => {
        // hide/show archive permissions if UAR1 is set
        if (this.form.current) {
          const fec = this.form.current.fec.current;
          if (fec.manager.data.archive) {
            const hidden = !this.hasArchivePermission(res.data);
            if (hidden !== fec.manager.data.archive.hidden) {
              fec.manager.data.archive.hidden = hidden;
              fec.syncForm();
            }
          }
        } else {
          this.layouts[activeBranch.branchId][4].hidden = !this.hasArchivePermission(res.data);
        }

        this.setState({ permissions: res.data, activeBranch: activeBranch || this.state.activeBranch });
        return Promise.resolve(res);
      })
      .finally(() => {
        this.setState({ isReady: true });
      });
  }

  getAllowedPermissions() {
    const { permissions } = this.state;
    const allowed = [];

    _.each(permissions, (val, key) => {
      if (val) {
        allowed.push(key);
      }
    });

    return allowed;
  }

  hasArchivePermission(permissions) {
    return permissions && (permissions.UPA1 || permissions.UPA3);
  }

  handleChange = ({ form }) => {
    const data = {};
    for (const field of ['roles', 'allow', 'deny']) {
      data[field] = form[field].data;
    }
    this.getPermissionMap(data);
  };

  initLayouts() {
    const { userId, searchData } = this.props;
    const where = btoa(JSON.stringify({ userId }));

    return Api.get('db/UST3R', {
      params: { where },
    }).then(res => {
      const { list } = res.data;
      if (!list || !list.length) {
        this.setState({ error: 'User must be associated with at least one branch!' });
        return;
      }

      for (const branch of list) {
        const roles = branch.roles || [];
        const docTypes = branch.archive || [];
        // filter out erroneous role data
        const roleIds = roles
          .filter(x => x)
          .filter(x => Object.keys(x).length)
          .map(role => String(role.id));
        const docTypeIds = docTypes.map(docType => String(docType.id));
        const userEqsRoles = branch.equipment || [];

        const layout = [
          {
            name: 'mvAccount',
            data: branch.mvAccount,
            label: 'Legacy Username',
            control: 'Input',
            helpText: 'Legacy system (qm/d3) username.',
            md: 6,
            sm: 6,
          },
          {
            collection: true,
            name: 'roles',
            data: roleIds,
            dataAlt: roles.map(role => role.name),
            label: 'Roles',
            control: 'Tags',
            validation: {
              type: 'db',
              modelName: 'Role',
              sortBy: 'name',
            },
            helpText: `
              Roles are groups of permissions.
              They will allow or deny access to various pages.
              Custom roles can be created on UST4.`,
          },
          {
            collection: true,
            name: 'allow',
            data: branch.allow || [],
            dataAlt: getPageTitles(branch.allow, searchData),
            label: 'Page Allow',
            control: 'Tags',
            pattern: '^(\\*|[A-Za-z0-9]+\\*?)$',
            validation: {
              type: 'page',
              optional: true,
            },
            helpText: `
              Allow access to specified pages.
              Use "*" to allow all pages.
              Use "A*" to allow access to pages that start with A.
              Use search to grant access to individual pages.`,
          },
          {
            collection: true,
            name: 'deny',
            data: branch.deny || [],
            dataAlt: getPageTitles(branch.deny, searchData),
            label: 'Page Deny',
            control: 'Tags',
            pattern: '^(\\*|[A-Za-z0-9]+\\*?)$',
            validation: {
              type: 'page',
              optional: true,
            },
            helpText: `
              Deny access to specified pages.
              Use "*" to deny all pages.
              Use "A*" to deny access to pages that start with A.
              Use search to deny access to individual pages.`,
          },
          {
            name: 'archive',
            data: docTypeIds,
            dataAlt: docTypes.map(docType => docType.name),
            label: 'Archive Document Types',
            control: 'Tags',
            validation: {
              allowSelectAll: true,
              type: 'db',
              modelName: 'ArchiveDocType',
              sortBy: 'name',
            },
            helpText: `
              Choose document types that user is allow to access from the archive`,
          },
          {
            name: 'equipment',
            data: userEqsRoles,
            dataAlt: userEqsRoles.map(userRole => eqsRoles.find(role => userRole === role.value)?.label),
            label: 'Equipment Sales Roles',
            control: 'Tags',
            validation: {
              allowSelectAll: true,
              type: 'static',
              suggestions: [
                {
                  label: 'Sales Manager',
                  value: 'SM',
                },
                {
                  label: 'Sales Person',
                  value: 'SP',
                },
                {
                  label: 'Accounting',
                  value: 'A',
                },
              ],
            },
          },
        ];

        this.layouts[branch.branchId] = layout;
        this.permissionData[branch.branchId] = {
          allow: branch.allow || [],
          deny: branch.deny || [],
          roles: roleIds || [],
        };
      }

      this.branches = _.keyBy(list, 'branchId');

      if (!this.state.activeBranch.branchId) {
        const activeBranch = list[0];
        // this.setState({ activeBranch });
        this.getPermissionMap(this.permissionData[activeBranch.branchId], activeBranch);
      }
    });
  }

  switchBranch = event => {
    const { value } = event.target;
    const form = this.form.current;

    const action = () => {
      this.setState({ isReady: false });
      this.getPermissionMap(this.permissionData[this.branches[value].branchId], this.branches[value]);
    };

    if (form.manager.isDirty()) {
      modal.confirm({
        message: 'You have unsaved changes! Changing the branch will discard these changes.',
        callback: action,
      });
    } else {
      action();
    }
  };

  submit = payload => {
    const { userId } = this.props;
    const { activeBranch, allBranches } = this.state;

    // transform payload into simple key: value json
    const updateData = {};
    for (const field of payload) {
      updateData[field.name] = field.data;
    }

    const branchId = activeBranch.branchId;
    const conditions = allBranches ? { userId } : { userId, branchId };
    const where = btoa(JSON.stringify(conditions));

    return Api.put('db/UST3R', updateData, {
      params: { where },
    }).then(res => {
      this.initLayouts();
      this.setState({ hasSubmitted: true });
      return Promise.resolve({ payload: res });
    });
  };

  handleRolesButton = () => {
    window.open(`${location.origin}/UST4`);
  };

  handleAllBranches = e => {
    const value = parseInt(e.target.value);
    if (value) {
      modal.confirm({
        title: 'Warning',
        message: `Selecting Yes will set permissions to all branches overwriting any previously set permissions
          and changes to 'Legacy Username' are ignored.`,
        size: 'md',
        callback: () => {
          this.setState({ allBranches: value });
        },
      });
    } else {
      this.setState({ allBranches: value });
    }
  };

  handleSummaryButton = () => {
    modal.info({
      title: 'Permission Summary',
      message: this.renderPermissions(),
      size: 'lg',
      dialogClass: 'fitted',
      hideFooter: true,
    });
  };

  renderPermissions() {
    const allowed = this.getAllowedPermissions().sort();

    if (allowed.length === 0) {
      return <div>No permissions specified</div>;
    }

    const cols = allowed.map((perm, i) => {
      const page = this.props.searchData[perm];
      if (!page) {
        // Should not happen but can
        return null;
      }

      return (
        <Col xs={12} sm={6} key={`perm-${i}`}>
          <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <b>{page.name}</b> - {page.title}
          </div>
        </Col>
      );
    });

    return <Row>{cols}</Row>;
  }

  render() {
    const { isNew, isReady, hasSubmitted, user, error, activeBranch } = this.state;

    if (!isReady || !activeBranch.branchId) {
      return <LoadingPage />;
    }

    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }

    const customActions = [
      {
        label: (
          <span>
            Permission Summary{' '}
            <Badge variant="primary" pill>
              {this.getAllowedPermissions().length}
            </Badge>
          </span>
        ),
        action: this.handleSummaryButton,
        style: 'info',
        title: 'Show all allowed pages (# of pages)',
        side: 'right',
      },
    ];

    if (isNew && hasSubmitted) {
      customActions.push({
        label: 'Create Another User',
        action: () => {
          browserHistory.push('/db/UST3/new');
        },
        style: 'success',
      });
    }

    return (
      <div className="d-flex flex-column flex-grow-1">
        <h3 style={{ padding: '0px 15px 15px 15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
          <i className="fas fa-users-crown" /> {user.firstName} {user.lastName} {user.username && `(${user.username})`}
        </h3>
        <Row style={{ padding: '15px' }}>
          <Col xs="auto" className="form-edit-container">
            <div className="d-inline-block form-select">
              <FormLabel style={{ fontWeight: 'bold' }}>Branch</FormLabel>
              <FormControl value={String(this.state.activeBranch.branchId)} onChange={this.switchBranch} as="select">
                {Object.keys(this.branches).map(branchId => {
                  return (
                    <option key={`branch-select-${branchId}`} value={branchId}>
                      {this.branches[branchId].company.name} - {this.branches[branchId].branch.name}
                    </option>
                  );
                })}
              </FormControl>
            </div>
          </Col>
          <Col xs="auto" className="form-edit-container">
            <div className="d-inline-block form-select">
              <FormLabel style={{ fontWeight: 'bold' }}>Apply to all branches</FormLabel>
              <FormControl value={this.state.allBranches} onChange={this.handleAllBranches} as="select">
                <option value="0">No</option>
                <option value="1">Yes</option>
              </FormControl>
            </div>
          </Col>
        </Row>

        <Form
          ref={this.form}
          key={String(this.state.activeBranch.branchId)}
          className="flex-grow-1"
          itemName={String(this.state.activeBranch.branchId)}
          layout={this.getCurrentLayout()}
          submit={this.submit}
          customActions={customActions}
          onChange={this.handleChange}
          submitAll
          hideCancel
        />
      </div>
    );
  }
}

UST3R.propTypes = {
  userId: PropTypes.number,
  searchData: PropTypes.object,
};

const mapStateToProps = state => {
  return {
    searchData: state.app.searchData,
  };
};

export default connect(mapStateToProps)(UST3R);
